import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import SignUpTrialOrgSetupStep from './trial/org-setup/SignUpAdvisorTrialOrgSetup'
import SignUpAdvisorStore, { SignUpAdvisorStoreProvider, AdvisorSignUpEnum } from './SignUpAdvisorStore'
import useStore from 'reusable/mobx-store-utils'
import AdvisorCreateAccount from './user/create-account/SignUpAdvisorCreateAccount'
import { PlatformContainer } from 'reusable/containers/PlatformContainer'
import useQuery from 'reusable/hooks/useQuery'
import { useEffect } from 'react'
import AuthAnalytics from 'analytics/AuthAnalytics'
import SignUpAdvisorMarketingContent from './trial/SignUpAdvisorMarketingContent'
import { getUtmPropertiesFromQueryString } from 'analytics/analytics'
import { useAuthStore } from 'auth/AuthStore'
import { useRegionRouterStore } from 'features/main/RegionRouterStore'
import { UserTrialSetupStepEnum } from '@gap/backend/app-services/user/models/UserTrialStepDto'
import SignUpAdvisorSelectOrg from './select-org/SignUpAdvisorSelectOrg'
import { observer } from 'mobx-react-lite'
import useInitialMount from 'reusable/hooks/useInitialMount'

export default observer(function SignUpAdvisor(props: { signUpAction: AdvisorSignUpEnum }) {
   const queryString = useQuery()
   const location = useLocation()
   const authStore = useAuthStore()
   const regionRouterStore = useRegionRouterStore()

   const signUpAdvisorStore = useStore((sp) => new SignUpAdvisorStore(sp), {
      signUpAction: props.signUpAction,
      queryString,
   })

   useEffect(() => {
      if (props.signUpAction === 'register' || signUpAdvisorStore.isInvitation) return

      const next = location.pathname.includes('org-setup')
         ? { step: 1, totalSteps: 2 }
         : null

      if (
         next &&
         (signUpAdvisorStore.progress?.step !== next.step ||
            signUpAdvisorStore.progress?.totalSteps !== next.totalSteps)
      ) {
         signUpAdvisorStore.progress = next
      }
   }, [location, props.signUpAction, signUpAdvisorStore])

   useEffect(() => {
      const isNewSignUp = signUpAdvisorStore.rememberRegistrationMethod()
      if (isNewSignUp && !signUpAdvisorStore.isInvitation) {
         AuthAnalytics.signupStarted({
            "Registration method": signUpAdvisorStore.registrationMethod,
         }, getUtmPropertiesFromQueryString(queryString))
      }
   }, [props.signUpAction, queryString, signUpAdvisorStore])


   useInitialMount(() => {
      if (authStore.isAuthenticated) {
         authStore.ensureUserIsOnCorrectTrialStep()
      }
   })

   const catchAllPath = !authStore.isAuthenticated
      ? '/trial'
      : authStore.shouldBeInTrialSetupFlow ? `/trial/${UserTrialSetupStepEnum.orgSetup}` : ''

   if (authStore.status === 'select org') {
      return <SignUpAdvisorStoreProvider store={signUpAdvisorStore}>
         <SignUpAdvisorSelectOrg />
      </SignUpAdvisorStoreProvider>
   }

   return <SignUpAdvisorStoreProvider store={signUpAdvisorStore}>
      <PlatformContainer variant="tinted" secondaryContent={<SignUpAdvisorMarketingContent isInvitation={signUpAdvisorStore.isInvitation} />} progress={signUpAdvisorStore.progress}>
         <Routes>
            {/* /trial or /register */}
            <Route path="/" element={<AdvisorCreateAccount />} />
            {/* Just here to identify page reload while verifying */}
            <Route path="verification" element={<AdvisorCreateAccount />} />

            {/* Trial steps. Submitting org setup restarts the trial clock, so keep finished users off it. */}
            <Route path="org-setup" element={
               authStore.hasFinishedTrialSetup
                  ? <Navigate to={`/${regionRouterStore.region}`} replace />
                  : <SignUpTrialOrgSetupStep />
            } />

            {/* Catch-all for undefined trial routes. */}
            <Route path="*" element={<Navigate to={`/${regionRouterStore.region}${catchAllPath}`} replace />} />
         </Routes>
      </PlatformContainer>
   </SignUpAdvisorStoreProvider>
})
